import { Image as ImageIcon, X } from "lucide-react";
import { useRef, useState } from "react";
import { bugsnagClient } from "../bugsnag";
import { ANALYTICS_EVENTS, trackEvent } from "../utils/analytics";
import api from "../utils/api";

const ImageUploadWizard = ({ onClose, projectId, onUploadComplete }) => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(
      (file) => file.type.startsWith("image/") && file.size <= 10000000, // 10MB limit
    );

    // Check if adding these files would exceed the 100 image limit
    if (selectedImages.length + validFiles.length > 100) {
      alert("You can upload a maximum of 100 images at once");
      // Only add files up to the limit
      const availableSlots = 100 - selectedImages.length;
      setSelectedImages((prev) => [
        ...prev,
        ...validFiles.slice(0, availableSlots),
      ]);
    } else {
      setSelectedImages((prev) => [...prev, ...validFiles]);
    }
  };

  const removeImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadImages = async () => {
    setUploading(true);
    setUploadProgress({});

    // Create a single FormData object for all images
    const formData = new FormData();
    selectedImages.forEach((file) => {
      formData.append("images", file);
    });
    formData.append("projectId", projectId);

    try {
      const result = await api.uploadImages(projectId, formData);

      trackEvent(ANALYTICS_EVENTS.IMAGE_UPLOADED, {
        projectId,
        imageCount: selectedImages.length,
      });
      setUploadResult(result);

      // Set all images as successfully uploaded
      const newProgress = {};
      selectedImages.forEach((_, index) => {
        newProgress[index] = 100;
      });
      setUploadProgress(newProgress);

      // Notify parent component about the upload
      if (onUploadComplete) {
        onUploadComplete(result);
      }

      // Close the modal after a longer delay to show completion
      setTimeout(() => {
        onClose();
      }, 5000);
    } catch (error) {
      console.error("Upload error:", error);
      // Mark all as error
      const newProgress = {};
      selectedImages.forEach((_, index) => {
        newProgress[index] = "error";
      });
      setUploadProgress(newProgress);
      bugsnagClient.notify(error);
    }

    setUploading(false);
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Upload Images</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {uploading && (
          <div className="absolute inset-0 bg-white bg-opacity-80 flex flex-col items-center justify-center z-10 rounded-lg">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-lg font-medium text-blue-600">
              Uploading images...
            </p>
            <p className="text-sm text-gray-600 mt-2">
              {selectedImages.length} files being processed
            </p>
          </div>
        )}

        <div className="space-y-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              id="image-upload"
              ref={fileInputRef}
              onChange={handleImageSelect}
            />
            <label htmlFor="image-upload" className="cursor-pointer">
              <div className="space-y-2">
                <div className="mx-auto h-12 w-12 text-gray-400">
                  <ImageIcon className="h-12 w-12" />
                </div>
                <div className="text-gray-600">
                  <span className="text-blue-600 hover:text-blue-500">
                    Upload images
                  </span>{" "}
                  or drag and drop
                </div>
                <p className="text-xs text-gray-500">
                  JPG, PNG, GIF up to 10MB each (maximum 100 images)
                </p>
              </div>
            </label>
          </div>

          {selectedImages.length > 0 && (
            <div className="max-h-60 overflow-y-auto">
              <div className="grid grid-cols-3 gap-4 p-1">
                {selectedImages.map((file, index) => (
                  <div key={index} className="relative">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Preview ${index}`}
                      className="w-full h-32 object-cover rounded"
                    />
                    <button
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 bg-white rounded-full p-1"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    {uploadProgress[index] && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1">
                        {uploadProgress[index]}%
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {uploadResult && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800">
                Upload successful! Processing job initiated.
              </p>
              <p className="text-sm text-green-700 mt-1">
                Job ID: {uploadResult.processing?.jobId}
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              onClick={uploadImages}
              disabled={selectedImages.length === 0 || uploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400"
            >
              {uploading ? "Uploading..." : "Upload Images"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUploadWizard;
